import React, { useState, useRef, useEffect, useCallback } from 'react';
import eventBus from '../../core/EventBus';

// ── SVG Icons ──────────────────────────────────────────────
const DragIcon = () => (
  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="9" cy="5" r="2"/><circle cx="15" cy="5" r="2"/>
    <circle cx="9" cy="12" r="2"/><circle cx="15" cy="12" r="2"/>
    <circle cx="9" cy="19" r="2"/><circle cx="15" cy="19" r="2"/>
  </svg>
);

const EyeIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19M1 1l22 22"/>
  </svg>
);

const LockIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const UnlockIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/>
  </svg>
);

const DeleteIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
  </svg>
);

// ── Type Icons ─────────────────────────────────────────────
const TYPE_ICONS = {
  'i-text': (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 7V4h16v3M9 20h6M12 4v16"/>
    </svg>
  ),
  text: (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 7V4h16v3M9 20h6M12 4v16"/>
    </svg>
  ),
  textbox: (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 7V4h16v3M9 20h6M12 4v16"/>
    </svg>
  ),
  rect: (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
    </svg>
  ),
  circle: (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9"/>
    </svg>
  ),
  image: (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
    </svg>
  ),
  group: (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="14" width="8" height="8" rx="1"/>
    </svg>
  ),
  polygon: (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="12,2 22,8.5 22,15.5 12,22 2,15.5 2,8.5"/>
    </svg>
  ),
  path: (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 17c3.33-3.33 6.67-10 10-10s6.67 6.67 10 10"/>
    </svg>
  ),
  line: (
    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="5" y1="19" x2="19" y2="5"/>
    </svg>
  ),
};

const DEFAULT_ICON = (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
  </svg>
);

function getTypeIcon(type) {
  return TYPE_ICONS[type] || DEFAULT_ICON;
}

// ── LayerPanel Component ───────────────────────────────────
/**
 * Reusable layer panel with drag & drop, rename, visibility/lock toggles.
 *
 * Props:
 *  - layers: Array from LayerManager.getLayers() (top-to-bottom, index 0 = frontmost)
 *  - layerManager: LayerManager instance
 *  - activeObject: currently selected Fabric object (for highlighting)
 *  - compact: boolean — when true, uses slightly smaller sizing (for mobile)
 */
function LayerPanel({ layers = [], layerManager, activeObject, compact = false }) {
  const [dragLayerId, setDragLayerId] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [editingLayerId, setEditingLayerId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const renameInputRef = useRef(null);

  // Focus the rename input when editing starts
  useEffect(() => {
    if (editingLayerId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [editingLayerId]);

  // Commit rename
  const commitRename = useCallback(() => {
    if (editingLayerId && layerManager) {
      const trimmed = editingName.trim();
      if (trimmed) {
        layerManager.renameLayer(editingLayerId, trimmed);
      }
    }
    setEditingLayerId(null);
    setEditingName('');
  }, [editingLayerId, editingName, layerManager]);

  // Cancel rename on Escape
  const handleRenameKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingLayerId(null);
      setEditingName('');
    }
  }, [commitRename]);

  // Double-click to rename
  const handleDoubleClick = useCallback((layer) => {
    setEditingLayerId(layer.id);
    setEditingName(layer.name);
  }, []);

  // Select layer on canvas
  const handleSelect = useCallback((layer) => {
    if (editingLayerId) return; // don't select while renaming
    if (layerManager) {
      layerManager.selectLayer(layer.id);
    }
  }, [layerManager, editingLayerId]);

  // ── Drag & Drop ──────────────────────────────────────────
  const handleDragStart = useCallback((e, layer) => {
    setDragLayerId(layer.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', layer.id);
    // Make the drag image semi-transparent
    if (e.target) {
      e.target.style.opacity = '0.4';
    }
  }, []);

  const handleDragOver = useCallback((e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIdx(idx);
  }, []);

  const handleDragLeave = useCallback((idx) => {
    setDragOverIdx(prev => prev === idx ? null : prev);
  }, []);

  const handleDrop = useCallback((e, targetIdx) => {
    e.preventDefault();
    if (dragLayerId && layerManager && layers.length > 0) {
      const fromIdx = layers.findIndex(l => l.id === dragLayerId);
      if (fromIdx !== -1 && fromIdx !== targetIdx) {
        // layers[0] = top (highest z), layers[last] = bottom (lowest z)
        // Fabric z-index: fabricIndex = layers.length - 1 - listIndex
        const fabricNewIndex = layers.length - 1 - targetIdx;
        layerManager.reorder(dragLayerId, fabricNewIndex);
      }
    }
    setDragLayerId(null);
    setDragOverIdx(null);
  }, [dragLayerId, layerManager, layers]);

  const handleDragEnd = useCallback((e) => {
    if (e.target) {
      e.target.style.opacity = '';
    }
    setDragLayerId(null);
    setDragOverIdx(null);
  }, []);

  // Determine the active object's __uid for highlighting
  const activeId = activeObject?.__uid || null;

  // Row height
  const rowH = compact ? 32 : 36;

  if (!layers || layers.length === 0) {
    return (
      <div className="text-center py-6">
        <svg className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
        </svg>
        <p className="text-xs text-gray-400 dark:text-gray-500">Keine Objekte vorhanden</p>
        <p className="text-[10px] text-gray-300 dark:text-gray-600 mt-1">Fuege Formen, Text oder Bilder hinzu</p>
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {layers.map((layer, idx) => {
        const icon = getTypeIcon(layer.type);
        const isDragging = dragLayerId === layer.id;
        const isOver = dragOverIdx === idx && !isDragging;
        const isActive = layer.id === activeId;
        const isEditing = editingLayerId === layer.id;

        return (
          <div
            key={layer.id}
            draggable={!isEditing}
            onDragStart={(e) => handleDragStart(e, layer)}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDragLeave={() => handleDragLeave(idx)}
            onDrop={(e) => handleDrop(e, idx)}
            onDragEnd={handleDragEnd}
            onClick={() => handleSelect(layer)}
            className={[
              'flex items-center gap-1.5 px-2 rounded-lg group transition-all select-none',
              isDragging ? 'opacity-40 scale-95' : '',
              isOver ? 'border-t-2 border-blue-500' : 'border-t-2 border-transparent',
              isActive && !isDragging
                ? 'bg-blue-50 dark:bg-blue-900/30 ring-1 ring-blue-400/40'
                : 'hover:bg-gray-50 dark:hover:bg-white/5',
              isEditing ? '' : 'cursor-grab active:cursor-grabbing',
            ].join(' ')}
            style={{ height: rowH }}
          >
            {/* Drag handle */}
            <span className={`shrink-0 ${isActive ? 'text-blue-400 dark:text-blue-400' : 'text-gray-300 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-500'} cursor-grab`}>
              <DragIcon />
            </span>

            {/* Type icon */}
            <span className={`shrink-0 ${isActive ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}>
              {icon}
            </span>

            {/* Name — editable on double-click */}
            {isEditing ? (
              <input
                ref={renameInputRef}
                type="text"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onBlur={commitRename}
                onKeyDown={handleRenameKeyDown}
                className="flex-1 min-w-0 text-xs bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 border border-blue-400 rounded px-1 py-0.5 outline-none"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span
                onDoubleClick={(e) => { e.stopPropagation(); handleDoubleClick(layer); }}
                className={[
                  'flex-1 text-xs truncate',
                  !layer.visible ? 'text-gray-300 dark:text-gray-600 line-through' : '',
                  isActive ? 'text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-300',
                ].join(' ')}
                title={`Doppelklick zum Umbenennen: ${layer.name}`}
              >
                {layer.name}
              </span>
            )}

            {/* Visibility toggle */}
            <button
              onClick={(e) => { e.stopPropagation(); layerManager && layerManager.setVisibility(layer.id, !layer.visible); }}
              className={`w-5 h-5 flex items-center justify-center rounded transition-colors shrink-0 ${
                layer.visible
                  ? 'text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400'
                  : 'text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400'
              }`}
              title={layer.visible ? 'Ausblenden' : 'Einblenden'}
            >
              {layer.visible ? <EyeIcon /> : <EyeOffIcon />}
            </button>

            {/* Lock toggle */}
            <button
              onClick={(e) => { e.stopPropagation(); layerManager && layerManager.setLocked(layer.id, !layer.locked); }}
              className={`w-5 h-5 flex items-center justify-center rounded transition-colors shrink-0 ${
                layer.locked
                  ? 'text-orange-400 dark:text-orange-400'
                  : 'text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400'
              }`}
              title={layer.locked ? 'Entsperren' : 'Sperren'}
            >
              {layer.locked ? <LockIcon /> : <UnlockIcon />}
            </button>

            {/* Delete */}
            <button
              onClick={(e) => { e.stopPropagation(); layerManager && layerManager.deleteLayer(layer.id); }}
              className="w-5 h-5 flex items-center justify-center rounded text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 transition-colors shrink-0"
              title="Loeschen"
            >
              <DeleteIcon />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default LayerPanel;
